/**
 * Mathematical Drawing Compass for Geometric Angle Bisectors, Circles, and Arcs.
 */
import { SVGObject, SVGObjectConfig } from './SVGObject';
import { Renderer } from '../core/Renderer';

export interface CompassProps {
  needleX?: number;
  needleY?: number;
  span?: number; // Distance in pixels between needle and pencil
  armLength?: number;
  rotation?: number; // Angle around needle in degrees
  pencilDown?: boolean;
}

export class Compass extends SVGObject {
  public props: CompassProps;

  constructor(config: SVGObjectConfig & { props?: CompassProps }) {
    super({ ...config, type: 'compass', layer: 'foreground' });
    this.props = {
      needleX: 0,
      needleY: 0,
      span: 120,
      armLength: 180,
      rotation: 0,
      pencilDown: true,
      ...config.props
    };
  }

  public render(): void {
    this.container.innerHTML = '';
    const span = this.props.span || 120;
    const armLen = this.props.armLength || 180;
    const halfSpan = span / 2;
    const hingeH = Math.sqrt(Math.max(10, armLen * armLen - halfSpan * halfSpan));

    const g = Renderer.createElement('g');
    Renderer.setAttributes(g, {
      transform: `translate(${this.props.needleX || 0}, ${this.props.needleY || 0}) rotate(${this.props.rotation || 0})`
    });

    // 1. Needle Leg (from needle (0,0) to hinge (halfSpan, -hingeH))
    const needleLeg = Renderer.createElement('path');
    Renderer.setAttributes(needleLeg, {
      d: `M 0 0 L ${halfSpan} ${-hingeH}`,
      stroke: '#94a3b8',
      'stroke-width': 6,
      'stroke-linecap': 'round'
    });
    g.appendChild(needleLeg);

    // Sharp needle tip
    const needleTip = Renderer.createElement('circle');
    Renderer.setAttributes(needleTip, {
      cx: 0,
      cy: 0,
      r: 3,
      fill: '#e2e8f0',
      stroke: '#475569',
      'stroke-width': 1
    });
    g.appendChild(needleTip);

    // 2. Pencil Leg (from hinge (halfSpan, -hingeH) to pencil (span, 0))
    const pencilLeg = Renderer.createElement('path');
    Renderer.setAttributes(pencilLeg, {
      d: `M ${halfSpan} ${-hingeH} L ${span} 0`,
      stroke: '#64748b',
      'stroke-width': 6,
      'stroke-linecap': 'round'
    });
    g.appendChild(pencilLeg);

    // Pencil body & Lead Tip
    const pencilBody = Renderer.createElement('rect');
    Renderer.setAttributes(pencilBody, {
      x: span - 4,
      y: -35,
      width: 8,
      height: 35,
      rx: 2,
      ry: 2,
      fill: '#f59e0b',
      stroke: '#d97706',
      'stroke-width': 1
    });
    g.appendChild(pencilBody);

    const leadTip = Renderer.createElement('polygon');
    Renderer.setAttributes(leadTip, {
      points: `${span - 4},-4 ${span + 4},-4 ${span},0`,
      fill: '#1e293b'
    });
    g.appendChild(leadTip);

    // 3. Top Brass Hinge & Knob
    const hinge = Renderer.createElement('circle');
    Renderer.setAttributes(hinge, {
      cx: halfSpan,
      cy: -hingeH,
      r: 10,
      fill: '#fbbf24',
      stroke: '#d97706',
      'stroke-width': 2
    });
    g.appendChild(hinge);

    const knob = Renderer.createElement('rect');
    Renderer.setAttributes(knob, {
      x: halfSpan - 3,
      y: -hingeH - 22,
      width: 6,
      height: 16,
      rx: 2,
      ry: 2,
      fill: '#fbbf24',
      stroke: '#b45309',
      'stroke-width': 1
    });
    g.appendChild(knob);

    this.container.appendChild(g);
  }

  public setNeedlePos(x: number, y: number): void {
    this.props.needleX = x;
    this.props.needleY = y;
    this.render();
  }

  public setSpan(span: number): void {
    this.props.span = span;
    this.render();
  }

  public setCompassRotation(deg: number): void {
    this.props.rotation = deg;
    this.render();
  }

  public getPencilPoint(): { x: number; y: number } {
    const rad = ((this.props.rotation || 0) * Math.PI) / 180;
    const span = this.props.span || 120;
    return {
      x: (this.props.needleX || 0) + Math.cos(rad) * span,
      y: (this.props.needleY || 0) + Math.sin(rad) * span
    };
  }
}
